import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useUndoHistory — pile Annuler / Rétablir de snapshots (géométrie d'un éditeur), commune aux éditeurs du plan de masse.
 * push(snapshotAvantModif) à chaque action ; undo(current) / redo(current) renvoient le snapshot à restaurer (ou undefined).
 */
export const useUndoHistory = (limit = 50) => {
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [, bump] = useState(0);
  const tick = () => bump((n) => n + 1);
  const push = useCallback((snapshot) => { undoRef.current = [...undoRef.current, snapshot].slice(-limit); redoRef.current = []; tick(); }, [limit]);
  const undo = useCallback((current) => {
    if (!undoRef.current.length) return undefined;
    const snap = undoRef.current[undoRef.current.length - 1];
    undoRef.current = undoRef.current.slice(0, -1); redoRef.current = [...redoRef.current, current]; tick();
    return snap;
  }, []);
  const redo = useCallback((current) => {
    if (!redoRef.current.length) return undefined;
    const snap = redoRef.current[redoRef.current.length - 1];
    redoRef.current = redoRef.current.slice(0, -1); undoRef.current = [...undoRef.current, current].slice(-limit); tick();
    return snap;
  }, [limit]);
  const clear = useCallback(() => { undoRef.current = []; redoRef.current = []; tick(); }, []);
  return { push, undo, redo, clear, canUndo: undoRef.current.length > 0, canRedo: redoRef.current.length > 0, depth: undoRef.current.length };
};

/** Ctrl/⌘+Z = annuler · Ctrl/⌘+Y ou Ctrl/⌘+Shift+Z = rétablir (ignorés dans les champs de saisie). */
export const useUndoRedoShortcuts = (onUndo, onRedo, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '') || e.target?.isContentEditable) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); onUndo?.(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); onRedo?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onUndo, onRedo, enabled]);
};
